import React, { useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PersonIcon from "@mui/icons-material/Person";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import RightSideCardShell from "./RightSideCardShell";
import AnimatedRainbowBorder from "./AnimatedRainbowBorder";
import { GOOGLE_APPS_SCRIPT_WEB_APP_URL } from "../googleAppsScriptWebApp";
import { trackEvent } from "../analytics";

interface FeedbackPopUpProps {
  onClose: () => void;
}

const FeedbackPopUp: React.FC<FeedbackPopUpProps> = ({ onClose }) => {
  const [message, setMessage] = useState("");
  const [nickname, setNickname] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const requestCloseRef = useRef<(() => void) | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    trackEvent("feedback_submit", { has_email: email.trim().length > 0 });

    try {
      await fetch(GOOGLE_APPS_SCRIPT_WEB_APP_URL, {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify({
          formType: "feedback",
          message: message.trim(),
          nickname: nickname.trim(),
          email: email.trim(),
          timestamp: new Date().toISOString(),
        }),
        headers: {
          "Content-Type": "text/plain",
        },
      });

      setSuccess(true);
      setTimeout(() => {
        requestCloseRef.current?.();
      }, 2000);
    } catch (err) {
      console.error("Feedback submission error:", err);
      setError("Failed to send. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RightSideCardShell
      onClose={onClose}
      widthClassName="w-[350px]"
      heightClassName="h-auto"
      panelStyle={{ height: "auto", maxHeight: "calc(100vh - 56px)" }}
    >
      {(requestClose) => {
        requestCloseRef.current = requestClose;

        return (
          <AnimatedRainbowBorder className="gilroy-medium" contentClassName="p-6">
            {success && (
              <div className="min-h-[280px] flex items-center justify-center">
                <div className="text-black gilroy-bold text-center text-xl">
                  Thanks — we read
                  <br />
                  every note.
                </div>
              </div>
            )}
            {!success && (
              <div>
                <div className="flex justify-between items-start pb-6">
                  <div className="text-xl text-black mb-2 pr-2" style={{ fontFamily: "'Kurdis Bold'" }}>
                    Feedback / ideas
                  </div>
                  <button
                    type="button"
                    onClick={requestClose}
                    className="text-black rounded-full h-[24px] w-[24px] flex items-center justify-center border border-black shrink-0"
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2 gilroy-medium">
                      What&apos;s on your mind?
                    </label>
                    <div className="relative">
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black text-black py-3 pl-9 pr-4 min-h-[120px] resize-y"
                        placeholder="Ideas, bugs, love letters to the button…"
                        required
                        maxLength={8000}
                      />
                      <ChatBubbleOutlineIcon
                        className="absolute left-3 top-4 text-gray-400"
                        sx={{ fontSize: 20 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Nickname</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}
                        className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-4 focus:outline-none focus:ring-2 focus:ring-black text-black"
                        placeholder="What should we call you?"
                        maxLength={120}
                        autoComplete="nickname"
                      />
                      <PersonIcon
                        className="absolute top-3 left-3 text-gray-400"
                        sx={{ fontSize: 20 }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">
                      Email{" "}
                      <span className="text-gray-500 font-normal">(optional)</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2 gilroy-regular leading-snug">
                      Only if you&apos;re ok with us reaching out for more detail.
                    </p>
                    <div className="relative">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-4 focus:outline-none focus:ring-2 focus:ring-black text-black"
                        placeholder="you@example.com"
                        maxLength={254}
                        autoComplete="email"
                      />
                      <EmailOutlinedIcon
                        className="absolute top-3 left-3 text-gray-400"
                        sx={{ fontSize: 20 }}
                      />
                    </div>
                  </div>
                  {error && <div className="text-red-500 text-sm">{error}</div>}
                  <div className="flex justify-center pt-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 w-full gilroy-medium"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending…" : "Send it"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </AnimatedRainbowBorder>
        );
      }}
    </RightSideCardShell>
  );
};

export default FeedbackPopUp;
